import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import VoiceSelector from '../components/VoiceSelector';
import ExportPublishPanel from '../components/ExportPublishPanel';
import AppShell from '../components/AppShell';
import { 
  Plus, Trash2, Sparkles,
  Loader2, Download, Copy, Check, Play, Image as ImageIcon, Lock, FolderOpen, Video
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReelsPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [reels, setReels] = useState([]);
  const [influencers, setInfluencers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [generatingVisual, setGeneratingVisual] = useState(null);
  const [renderingVideo, setRenderingVideo] = useState(null);
  const [copiedScript, setCopiedScript] = useState(null);
  const [selectedReel, setSelectedReel] = useState(null);
  const [platforms, setPlatforms] = useState([]);
  const [categories, setCategories] = useState([]);

  const [formData, setFormData] = useState({
    prompt: '',
    influencer_id: '',
    language: 'auto',
    voice: 'warm_narrator',
    voice_mode: 'standard',
    platform: 'instagram',
    category: 'general',
    ugc_mode: false,
    ugc_preset: 'product_testimonial',
    ugc_caption_size: 1.0,
    ugc_highlight_hook: false,
  });

  const [ugcInfo, setUgcInfo] = useState(null);

  // Voice options removed - now using VoiceSelector component with dynamic voices from API

  const platformIcons = {
    instagram: '📸',
    tiktok: '🎵',
    youtube_shorts: '▶️',
    facebook: '📘'
  };

  useEffect(() => {
    fetchData();
    fetchPlatforms();
    fetchUgcInfo();
    // Poll for rendering updates
    const interval = setInterval(() => {
      const hasRendering = reels.some(r => ['queued', 'rendering', 'interpreting', 'generating_script'].includes(r.status));
      if (hasRendering) fetchData(true);
    }, 5000);
    return () => clearInterval(interval);
  }, [token, reels.length]);

  const fetchUgcInfo = async () => {
    try {
      const r = await axios.get(`${API}/reels/ugc/info`, { headers: { Authorization: `Bearer ${token}` } });
      setUgcInfo(r.data);
    } catch {}
  };

  const fetchPlatforms = async () => {
    try {
      const response = await axios.get(`${API}/platforms`);
      setPlatforms(response.data.platforms || []);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch platforms');
    }
  };

  const fetchData = async (silent = false) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [reelsRes, influencersRes] = await Promise.all([
        axios.get(`${API}/reels/`, { headers }),
        axios.get(`${API}/influencers/`, { headers })
      ]);
      setReels(reelsRes.data);
      setInfluencers(influencersRes.data);
    } catch (error) {
      if (!silent) toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.prompt.trim()) {
      toast.error('Please describe what you want to create');
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API}/reels/`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReels([response.data, ...reels]);
      setCreateDialogOpen(false);
      setFormData({ prompt: '', influencer_id: '', language: 'auto', voice: 'warm_narrator', voice_mode: 'standard', platform: 'instagram', category: 'general', ugc_mode: false, ugc_preset: 'product_testimonial', ugc_caption_size: 1.0, ugc_highlight_hook: false });
      
      const platformName = platforms.find(p => p.id === formData.platform)?.name || formData.platform;
      toast.success(`Delphi is creating your ${platformName} content with background music...`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create content');
    } finally {
      setCreating(false);
    }
  };

  const handleRenderVideo = async (reelId) => {
    setRenderingVideo(reelId);
    try {
      await axios.post(
        `${API}/reels/${reelId}/render-video`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Video rendering started!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start rendering');
    } finally {
      setRenderingVideo(null);
    }
  };

  const handleGenerateVisual = async (reelId) => {
    setGeneratingVisual(reelId);
    try {
      const response = await axios.post(
        `${API}/reels/${reelId}/generate-visual`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setReels(reels.map(reel => 
        reel.id === reelId ? { ...reel, image_url: response.data.image_url, status: 'visual_ready' } : reel
      ));
      toast.success('Visual generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate visual');
    } finally {
      setGeneratingVisual(null);
    }
  };

  const handleDelete = async (reelId) => {
    if (!window.confirm('Delete this reel?')) return;
    
    try {
      await axios.delete(`${API}/reels/${reelId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReels(reels.filter(reel => reel.id !== reelId));
      toast.success('Reel deleted');
    } catch (error) {
      toast.error('Failed to delete reel');
    }
  };

  const copyScript = (script, reelId) => {
    navigator.clipboard.writeText(script);
    setCopiedScript(reelId);
    toast.success('Script copied!');
    setTimeout(() => setCopiedScript(null), 2000);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'visual_ready': return 'bg-green-500/20 text-green-400';
      case 'script_ready': return 'bg-blue-500/20 text-blue-400';
      case 'error': return 'bg-red-500/20 text-red-400';
      case 'processing': return 'bg-yellow-500/20 text-yellow-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  return (
    <AppShell>
      <div className="p-5 sm:p-7 lg:p-10">
          {/* Header */}
          <div className="flex items-center justify-between mb-10">
            <div>
              <p className="text-[10px] text-primary/60 uppercase tracking-[0.2em] font-semibold mb-3">Content Engine</p>
              <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">Content Engine</h1>
              <p className="text-zinc-500 text-sm mt-2">
                Create SEO-optimized content for Instagram, TikTok, YouTube & Facebook
              </p>
            </div>
            
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-oracle" data-testid="create-reel-btn">
                  <Plus className="w-5 h-5 mr-2" /> Create Content
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-xl font-bold">Create New Content</DialogTitle>
                </DialogHeader>
                
                <div className="space-y-6 mt-4">
                  {/* Platform Selection */}
                  <div className="space-y-2">
                    <Label>Target Platform *</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {platforms.map(platform => (
                        <button
                          key={platform.id}
                          type="button"
                          onClick={() => setFormData({ ...formData, platform: platform.id })}
                          className={`p-3 rounded-xl border text-left transition-all ${
                            formData.platform === platform.id 
                              ? 'border-primary bg-primary/10' 
                              : 'border-border hover:border-primary/50'
                          }`}
                          data-testid={`platform-${platform.id}`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{platformIcons[platform.id]}</span>
                            <span className="font-medium text-sm">{platform.name}</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">{platform.description}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Prompt Input - REQUIRED */}
                  <div className="space-y-2">
                    <Label>Describe what you want to create *</Label>
                    <Textarea
                      data-testid="reel-prompt-input"
                      placeholder="Tell Delphi what you want, in plain language. For example:&#10;• 'A fun video about why mornings are the best time to be productive'&#10;• 'Show 5 quick tips for better sleep with calming visuals'&#10;• 'Create an energetic ad for my new fitness app'"
                      value={formData.prompt}
                      onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
                      className="input-oracle min-h-[120px]"
                      required
                    />
                    <p className="text-xs text-muted-foreground">
                      Delphi will interpret your prompt and handle the rest - script, visuals, voice, and background music.
                    </p>
                  </div>

                  {/* Category for SEO */}
                  <div className="space-y-2">
                    <Label>Content Category</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => setFormData({ ...formData, category: value })}
                    >
                      <SelectTrigger className="input-oracle" data-testid="category-select">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        {categories.map(cat => (
                          <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {influencers.length > 0 && (
                    <div className="space-y-2">
                      <Label>Influencer (Optional)</Label>
                      <Select
                        value={formData.influencer_id || "none"}
                        onValueChange={(value) => setFormData({ ...formData, influencer_id: value === "none" ? "" : value })}
                      >
                        <SelectTrigger data-testid="reel-influencer-select" className="input-oracle">
                          <SelectValue placeholder="Select an influencer" />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          <SelectItem value="none">No specific influencer</SelectItem>
                          {influencers.map(inf => (
                            <SelectItem key={inf.id} value={inf.id}>{inf.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Language</Label>
                    <Select
                      value={formData.language}
                      onValueChange={(value) => setFormData({ ...formData, language: value })}
                    >
                      <SelectTrigger className="input-oracle">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        <SelectItem value="auto">Auto-detect</SelectItem>
                        <SelectItem value="en">English</SelectItem>
                        <SelectItem value="es">Spanish</SelectItem>
                        <SelectItem value="fr">French</SelectItem>
                        <SelectItem value="de">German</SelectItem>
                        <SelectItem value="pt">Portuguese</SelectItem>
                        <SelectItem value="zh">Chinese</SelectItem>
                        <SelectItem value="ja">Japanese</SelectItem>
                        <SelectItem value="ko">Korean</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Voice Selection with Premium Toggle */}
                  <VoiceSelector
                    selectedVoice={formData.voice}
                    onVoiceChange={(voice) => setFormData({ ...formData, voice })}
                    voiceMode={formData.voice_mode}
                    onVoiceModeChange={(mode) => setFormData({ ...formData, voice_mode: mode })}
                    showCompact={true}
                  />

                  {/* ═══════ UGC MODE TOGGLE ═══════ */}
                  {ugcInfo && (
                    <div className={`relative border-2 rounded-xl overflow-hidden transition-all duration-300 ${formData.ugc_mode ? 'border-primary/50 bg-gradient-to-b from-primary/[0.06] to-primary/[0.02] shadow-[0_0_32px_-6px_rgba(249,115,22,0.25),inset_0_1px_0_rgba(249,115,22,0.1)]' : 'border-white/[0.04] bg-white/[0.015]'}`} data-testid="ugc-section">
                      {formData.ugc_mode && (
                        <>
                          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary/60 to-transparent" />
                          <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
                        </>
                      )}
                      <div className="flex items-center justify-between px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-[9px] font-bold tracking-wider transition-all duration-300 ${formData.ugc_mode ? 'bg-primary/20 text-primary shadow-[0_0_12px_-3px_rgba(249,115,22,0.4)]' : 'bg-zinc-800 text-zinc-500'}`}>
                            UGC
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-zinc-200">UGC Mode</p>
                              {formData.ugc_mode && (
                                <span className="text-[9px] font-bold text-primary uppercase tracking-[0.12em] px-2 py-0.5 rounded-full bg-primary/[0.12] border border-primary/20 shadow-[0_0_8px_-2px_rgba(249,115,22,0.3)] animate-pulse-glow" data-testid="ugc-active-badge">UGC MODE ACTIVE</span>
                              )}
                            </div>
                            <p className="text-[10px] text-zinc-500">Ad-style, high-converting short videos</p>
                          </div>
                        </div>
                        {ugcInfo.ugc_available ? (
                          <button
                            onClick={() => setFormData(f => ({ ...f, ugc_mode: !f.ugc_mode }))}
                            className={`relative w-10 rounded-full transition-all duration-300 ${formData.ugc_mode ? 'bg-primary shadow-[0_0_16px_-2px_rgba(249,115,22,0.5)]' : 'bg-zinc-700'}`}
                            style={{ height: '22px' }}
                            data-testid="ugc-toggle"
                          >
                            <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform duration-300 ${formData.ugc_mode ? 'translate-x-5' : 'translate-x-1'}`} style={{ height: '18px', width: '18px' }} />
                          </button>
                        ) : (
                          <div className="relative group" data-testid="ugc-locked">
                            <div className="px-2.5 py-1 rounded-md bg-zinc-800 text-zinc-600 text-[10px] font-medium cursor-not-allowed flex items-center gap-1">
                              <Lock className="w-2.5 h-2.5" /> Pro+
                            </div>
                            <div className="absolute bottom-full right-0 mb-2 w-56 p-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-[11px] text-zinc-300 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                              Unlock UGC Mode in Pro to create high-converting ad-style videos.
                            </div>
                          </div>
                        )}
                      </div>

                      {/* UGC Options — only when enabled */}
                      {formData.ugc_mode && ugcInfo.ugc_available && (
                        <div className="px-4 pb-4 space-y-3 border-t border-orange-500/10 pt-3">
                          {/* Preset Selector */}
                          <div className="space-y-1.5">
                            <Label className="text-xs text-zinc-400">UGC Preset</Label>
                            <p className="text-[10px] text-zinc-600 -mt-0.5">Uses persuasion-based scripting frameworks.</p>
                            <Select
                              value={formData.ugc_preset}
                              onValueChange={(v) => setFormData(f => ({ ...f, ugc_preset: v }))}
                            >
                              <SelectTrigger className="input-oracle h-9 text-xs" data-testid="ugc-preset-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-card border-border">
                                {ugcInfo.presets.map(p => (
                                  <SelectItem key={p.id} value={p.id}>
                                    <div>
                                      <span className="font-medium">{p.name}</span>
                                      <span className="text-zinc-500 ml-1.5 text-[10px]">{p.description}</span>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Caption Size Slider */}
                          <div className="space-y-1.5">
                            <div className="flex justify-between items-center">
                              <Label className="text-xs text-zinc-400">Caption Size</Label>
                              <span className="text-[10px] text-zinc-500">{Math.round(formData.ugc_caption_size * 100)}%</span>
                            </div>
                            <input
                              type="range"
                              min="1"
                              max="1.5"
                              step="0.1"
                              value={formData.ugc_caption_size}
                              onChange={(e) => setFormData(f => ({ ...f, ugc_caption_size: parseFloat(e.target.value) }))}
                              className="w-full h-1.5 bg-zinc-800 rounded-full appearance-none cursor-pointer accent-orange-500"
                              data-testid="ugc-caption-slider"
                            />
                          </div>

                          {/* Highlight Hook Toggle */}
                          <label className="flex items-center justify-between cursor-pointer" data-testid="ugc-highlight-hook">
                            <span className="text-xs text-zinc-400">Highlight hook line</span>
                            <button
                              type="button"
                              onClick={() => setFormData(f => ({ ...f, ugc_highlight_hook: !f.ugc_highlight_hook }))}
                              className={`relative w-8 rounded-full transition-colors ${formData.ugc_highlight_hook ? 'bg-orange-500' : 'bg-zinc-700'}`}
                              style={{ height: '18px' }}
                            >
                              <div className={`absolute top-0.5 w-3.5 h-3.5 rounded-full bg-white transition-transform ${formData.ugc_highlight_hook ? 'translate-x-4' : 'translate-x-0.5'}`} style={{ height: '14px', width: '14px' }} />
                            </button>
                          </label>

                          {/* Credit Breakdown */}
                          <div className="bg-zinc-950/50 rounded-lg p-2.5 space-y-1" data-testid="ugc-credit-breakdown">
                            <div className="flex justify-between text-[11px]">
                              <span className="text-zinc-500">Base video cost</span>
                              <span className="text-zinc-400">10 credits</span>
                            </div>
                            <div className="flex justify-between text-[11px]">
                              <span className="text-orange-400/80">UGC optimization</span>
                              <span className="text-orange-400">+{ugcInfo.credit_surcharge} credits</span>
                            </div>
                            <div className="border-t border-zinc-800 pt-1 flex justify-between text-xs font-medium">
                              <span className="text-zinc-300">Total cost</span>
                              <span className="text-white">{10 + ugcInfo.credit_surcharge} credits</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* What Delphi will do */}
                  <div className="p-4 bg-primary/5 border border-primary/20 rounded-xl">
                    <p className="font-medium text-primary mb-2">{formData.ugc_mode ? 'UGC Mode — Delphi will create:' : 'What Delphi will create:'}</p>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>✓ AI-generated script from your prompt</li>
                      <li>✓ Custom visuals for each scene</li>
                      <li>✓ Professional voiceover</li>
                      <li>✓ Background music (auto-matched to mood)</li>
                      <li>✓ SEO-optimized captions & hashtags</li>
                      {formData.ugc_mode && (
                        <>
                          <li className="text-orange-400">✓ Conversion-optimized hook & CTA</li>
                          <li className="text-orange-400">✓ Fast-paced UGC editing style</li>
                          <li className="text-orange-400">✓ Dynamic captions with emphasis</li>
                        </>
                      )}
                    </ul>
                  </div>

                  <Button
                    onClick={handleCreate}
                    disabled={creating || !formData.prompt.trim()}
                    className="w-full btn-oracle h-12"
                    data-testid="confirm-create-reel-btn"
                  >
                    {creating ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Delphi is creating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5 mr-2" />
                        Create with Delphi
                      </>
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="card-oracle p-4">
              <p className="text-sm text-muted-foreground">Total Reels</p>
              <p className="text-2xl font-bold">{reels.length}</p>
            </div>
            <div className="card-oracle p-4">
              <p className="text-sm text-muted-foreground">Ready</p>
              <p className="text-2xl font-bold text-green-400">{reels.filter(r => r.status === 'ready').length}</p>
            </div>
            <div className="card-oracle p-4">
              <p className="text-sm text-muted-foreground">Rendering</p>
              <p className="text-2xl font-bold text-blue-400">{reels.filter(r => ['queued', 'rendering'].includes(r.status)).length}</p>
            </div>
            <Link to="/library" className="card-oracle p-4 hover:border-primary/50 transition-colors">
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <FolderOpen className="w-4 h-4" /> View Library
              </p>
              <p className="text-primary text-sm mt-1">See all content →</p>
            </Link>
          </div>

          {/* Reels Grid */}
          {loading ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="card-oracle p-6">
                  <div className="skeleton h-6 w-48 mb-4" />
                  <div className="skeleton h-32 w-full mb-4" />
                  <div className="skeleton h-10 w-full" />
                </div>
              ))}
            </div>
          ) : reels.length === 0 ? (
            <div className="text-center py-20">
              <Video className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2">No Content Yet</h2>
              <p className="text-muted-foreground mb-6">Create your first viral content in one click</p>
              <Button onClick={() => setCreateDialogOpen(true)} className="btn-oracle" data-testid="empty-create-reel-btn">
                <Plus className="w-5 h-5 mr-2" /> Create Content
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {reels.map((reel, i) => (
                <div key={reel.id} className="card-oracle p-6" data-testid={`reel-card-${i}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">{platformIcons[reel.platform] || '📸'}</span>
                        <h3 className="font-bold text-lg line-clamp-1">{reel.topic || reel.prompt}</h3>
                      </div>
                      {reel.prompt && reel.prompt !== reel.topic && (
                        <p className="text-xs text-muted-foreground line-clamp-2 mb-2 italic">&quot;{reel.prompt}&quot;</p>
                      )}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(reel.status)}`}>
                          {reel.status.replace('_', ' ')}
                        </span>
                        <span className="text-xs px-2 py-1 rounded-full bg-secondary text-muted-foreground">
                          {platforms.find(p => p.id === reel.platform)?.name || reel.platform || 'Instagram'}
                        </span>
                        {reel.mood && (
                          <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary">
                            {reel.mood}
                          </span>
                        )}
                        <span className="text-xs text-muted-foreground">
                          {new Date(reel.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <Button
                      onClick={() => handleDelete(reel.id)}
                      variant="ghost"
                      size="icon"
                      className="text-muted-foreground hover:text-destructive"
                      data-testid={`delete-reel-btn-${i}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Script */}
                  {reel.script && (
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-sm">Script</Label>
                        <Button
                          onClick={() => copyScript(reel.script, reel.id)}
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs"
                          data-testid={`copy-script-btn-${i}`}
                        >
                          {copiedScript === reel.id ? (
                            <><Check className="w-3 h-3 mr-1" /> Copied</>
                          ) : (
                            <><Copy className="w-3 h-3 mr-1" /> Copy</>
                          )}
                        </Button>
                      </div>
                      <div className="bg-secondary/30 rounded-xl p-4 text-sm max-h-40 overflow-y-auto">
                        {reel.script}
                      </div>
                    </div>
                  )}

                  {/* SEO Section - Hashtags and Caption */}
                  {reel.hashtags && reel.hashtags.length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-sm">SEO Hashtags</Label>
                        <Button
                          onClick={() => {
                            const hashtagStr = reel.hashtags.map(h => `#${h}`).join(' ');
                            navigator.clipboard.writeText(hashtagStr);
                            toast.success('Hashtags copied!');
                          }}
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs"
                          data-testid={`copy-hashtags-btn-${i}`}
                        >
                          <Copy className="w-3 h-3 mr-1" /> Copy
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {reel.hashtags.slice(0, 8).map((tag, idx) => (
                          <span key={idx} className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary">
                            #{tag}
                          </span>
                        ))}
                        {reel.hashtags.length > 8 && (
                          <span className="text-xs px-2 py-1 rounded-full bg-secondary text-muted-foreground">
                            +{reel.hashtags.length - 8} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Video Preview / Rendering Status */}
                  {reel.status === 'ready' && reel.video_url ? (
                    <div className="mb-4">
                      <Label className="text-sm mb-2 block">Video Ready</Label>
                      <div className="relative rounded-xl overflow-hidden bg-black">
                        <video
                          src={`${BACKEND_URL}${reel.video_url}`}
                          className="w-full aspect-video object-contain"
                          controls
                          preload="metadata"
                        />
                      </div>
                      <ExportPublishPanel
                        title={reel.topic}
                        description={reel.script}
                        hashtags={reel.hashtags}
                        downloadUrl={`${BACKEND_URL}${reel.video_url}/download`}
                        downloadLabel="Download MP4"
                      />
                    </div>
                  ) : ['queued', 'rendering'].includes(reel.status) ? (
                    <div className="mb-4 p-4 bg-secondary/30 rounded-xl">
                      <div className="flex items-center gap-3 mb-3">
                        <Loader2 className="w-6 h-6 text-primary animate-spin" />
                        <div>
                          <p className="font-medium">Rendering Video...</p>
                          <p className="text-sm text-muted-foreground">{reel.render_message || 'Processing...'}</p>
                        </div>
                      </div>
                      {reel.render_progress > 0 && (
                        <div className="w-full h-2 bg-secondary rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-primary transition-all duration-300"
                            style={{ width: `${reel.render_progress}%` }}
                          />
                        </div>
                      )}
                    </div>
                  ) : reel.image_url ? (
                    <div className="mb-4">
                      <Label className="text-sm mb-2 block">Visual</Label>
                      <div className="relative rounded-xl overflow-hidden">
                        <img
                          src={reel.image_url}
                          alt={reel.topic}
                          className="w-full aspect-video object-cover"
                        />
                        <a
                          href={reel.image_url}
                          download={`reel-${reel.id}.png`}
                          className="absolute bottom-2 right-2 p-2 bg-black/50 rounded-lg hover:bg-black/70 transition-colors"
                          data-testid={`download-visual-btn-${i}`}
                        >
                          <Download className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  ) : reel.status === 'script_ready' && (
                    <div className="flex gap-2 mb-4">
                      <Button
                        onClick={() => handleRenderVideo(reel.id)}
                        disabled={renderingVideo === reel.id}
                        className="flex-1 btn-oracle"
                        data-testid={`render-video-btn-${i}`}
                      >
                        {renderingVideo === reel.id ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Starting...
                          </>
                        ) : (
                          <>
                            <Play className="w-4 h-4 mr-2" />
                            Render Video
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={() => handleGenerateVisual(reel.id)}
                        disabled={generatingVisual === reel.id}
                        variant="outline"
                        className="border-border"
                        data-testid={`generate-visual-btn-${i}`}
                      >
                        {generatingVisual === reel.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <ImageIcon className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  )}

                  {/* Failed Status */}
                  {reel.status === 'failed' && (
                    <div className="mb-4 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                      <p className="text-sm text-red-400">{reel.render_message || 'Video rendering failed'}</p>
                      <Button
                        onClick={() => handleRenderVideo(reel.id)}
                        size="sm"
                        className="mt-2 bg-red-500/20 text-red-400 hover:bg-red-500/30"
                      >
                        Retry Rendering
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
    </AppShell>
  );
};

export default ReelsPage;
